// WorkshopTable.js - таблица данных для цеха

import React, { useState, useCallback, useMemo } from 'react';
import { Group, Line, Text, Rect, Circle } from 'react-konva';

/**
 * Компонент таблицы данных для цеха
 * @param {Object} props
 * @param {Object} props.workshopInfo - данные цеха {deal, deadline, city, quantity}
 * @param {number} props.weldingTotal - итоговая сумма сварки из стоимости (в рублях)
 * @param {boolean} props.isLandscape - ориентация
 * @param {Object} props.tableState - состояние таблицы
 * @param {Function} props.onTableStateChange - callback изменения состояния
 * @param {Function} props.onSelect - callback выбора таблицы
 * @param {boolean} props.isSelected - выбрана ли таблица
 */
const WorkshopTable = ({ 
  workshopInfo, 
  weldingTotal = 0,
  isLandscape,
  tableState,
  onTableStateChange,
  onSelect,
  isSelected = false
}) => {
  // Параметры таблицы в зависимости от ориентации
  const config = useMemo(() => {
    if (isLandscape) {
      return {
        x: 1327, // справа над артикулом (который на x: 1340, y: 925)
        y: 840, // над артикулом
        colWidths: [140, 100], // [название, значение]
        rowHeight: 28,
        fontSize: 18,
      };
    } else {
      return {
        x: 589, // справа над артикулом (который на x: 620, y: 982)
        y: 912, // над артикулом
        colWidths: [110, 80], // [название, значение]
        rowHeight: 25,
        fontSize: 14,
      };
    }
  }, [isLandscape]);

  const quantity = parseInt(workshopInfo?.quantity) || 0;
  
  // Вычисляем количество строк: всегда "Сварка", + "Количество" если > 1
  const hasQuantityRow = quantity > 1;
  const rowCount = hasQuantityRow ? 2 : 1;

  // Состояние по умолчанию
  const state = useMemo(() => {
    const defaultState = {
      x: config.x,
      y: config.y,
      colWidths: config.colWidths,
      rowHeight: config.rowHeight,
      fontSize: config.fontSize,
    };
    return { ...defaultState, ...tableState };
  }, [config, tableState]);

  const { x, y, colWidths, rowHeight, fontSize } = state;

  const tableWidth = colWidths.reduce((sum, w) => sum + w, 0);
  const tableHeight = rowHeight * rowCount;

  // Состояния для ресайза
  const [resizingCol, setResizingCol] = useState(null);
  const [resizingRow, setResizingRow] = useState(null);

  // Вычисляем позиции колонок
  const colPositions = [0];
  for (let i = 0; i < colWidths.length; i++) {
    colPositions.push(colPositions[i] + colWidths[i]);
  }

  // Форматирование суммы в рубли
  const formatRUB = (value) => {
    return `${Math.round(value).toLocaleString('ru-RU')} ₽`;
  };

  // Данные таблицы
  const rows = [];
  
  // Строка "Количество" (если > 1)
  if (hasQuantityRow) {
    rows.push({
      label: 'Количество',
      value: `${quantity} шт.`
    });
  }
  
  // Строка "Сварка" (всегда)
  rows.push({
    label: 'Сварка',
    value: formatRUB(weldingTotal)
  });

  // Обработчик начала ресайза колонки
  const handleColResizeStart = useCallback((colIndex) => {
    setResizingCol(colIndex);
  }, []);

  // Обработчик ресайза колонки
  const handleColResize = useCallback((e, colIndex) => {
    if (resizingCol !== colIndex) return;
    
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const localX = pointerPos.x - x;
    
    // Вычисляем новую ширину
    const prevColsWidth = colWidths.slice(0, colIndex).reduce((sum, w) => sum + w, 0);
    const newWidth = Math.max(50, localX - prevColsWidth);
    
    const newColWidths = [...colWidths];
    newColWidths[colIndex] = newWidth;
    
    if (onTableStateChange) {
      onTableStateChange({ ...state, colWidths: newColWidths });
    }
  }, [resizingCol, colWidths, state, onTableStateChange, x]);

  // Обработчик окончания ресайза
  const handleResizeEnd = useCallback(() => {
    setResizingCol(null);
    setResizingRow(null);
  }, []);

  // Обработчик изменения высоты строки
  const handleRowResize = useCallback((e) => {
    if (resizingRow === null) return;
    
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const localY = pointerPos.y - y;
    
    const newRowHeight = Math.max(20, localY / (resizingRow + 1));
    
    if (onTableStateChange) {
      onTableStateChange({ ...state, rowHeight: newRowHeight });
    }
  }, [resizingRow, state, onTableStateChange, y]);

  // Проверяем, нужно ли отображать таблицу (после ВСЕХ хуков)
  if (!workshopInfo) {
    return null;
  }

  return (
    <Group
      onMouseMove={(e) => {
        handleColResize(e, resizingCol);
        handleRowResize(e);
      }}
      onMouseUp={handleResizeEnd}
      onMouseLeave={handleResizeEnd}
    >
      {/* Основная группа таблицы */}
      <Group
        x={x}
        y={y}
        onClick={(e) => {
          e.cancelBubble = true;
          if (onSelect) onSelect();
        }}
      >
        {/* Фон таблицы */}
        <Rect
          x={0}
          y={0}
          width={tableWidth}
          height={tableHeight}
          fill="#ffffff"
          stroke="#000000"
          strokeWidth={2}
        />

        {/* Вертикальная линия между колонками */}
        <Line
          points={[colPositions[1], 0, colPositions[1], tableHeight]}
          stroke="#000000"
          strokeWidth={1}
          listening={false}
        />

        {/* Горизонтальные линии строк */}
        {rows.map((_, index) => (
          <Line
            key={`row-line-${index}`}
            points={[
              0, 
              rowHeight * (index + 1), 
              tableWidth, 
              rowHeight * (index + 1)
            ]}
            stroke="#cccccc"
            strokeWidth={0.5}
            listening={false}
          />
        ))}

        {/* Данные */}
        {rows.map((row, index) => {
          const rowY = rowHeight * index;

          return (
            <Group key={index}>
              {/* Название */}
              <Text
                x={10}
                y={rowY + (rowHeight - fontSize) / 2}
                text={row.label}
                fontSize={fontSize - 2}
                fontFamily="Arial"
                fill="#000000"
                width={colWidths[0] - 20}
                listening={false}
              />
              {/* Значение */}
              <Text
                x={colPositions[1] + 10}
                y={rowY + (rowHeight - fontSize) / 2}
                text={row.value}
                fontSize={fontSize - 2}
                fontFamily="Arial"
                fill="#000000"
                listening={false}
              />
            </Group>
          );
        })}

        {/* Ручки для изменения ширины колонок */}
        {colPositions.slice(1, -1).map((pos, index) => (
          <Rect
            key={`col-resize-${index}`}
            x={pos - 3}
            y={0}
            width={6}
            height={tableHeight}
            fill="transparent"
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'col-resize';
            }}
            onMouseLeave={(e) => {
              if (resizingCol === null) {
                const container = e.target.getStage().container();
                container.style.cursor = 'default';
              }
            }}
            onMouseDown={() => handleColResizeStart(index)}
          />
        ))}

        {/* Ручки для изменения высоты строк */}
        {rows.map((_, index) => (
          <Rect
            key={`row-resize-${index}`}
            x={0}
            y={rowHeight * (index + 1) - 3}
            width={tableWidth}
            height={6}
            fill="transparent"
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'row-resize';
            }}
            onMouseLeave={(e) => {
              if (resizingRow === null) {
                const container = e.target.getStage().container();
                container.style.cursor = 'default';
              }
            }}
            onMouseDown={() => setResizingRow(index)}
          />
        ))}

        {/* Ручка для изменения ширины правого столбца (правая граница таблицы) */}
        <Rect
          x={tableWidth - 3}
          y={0}
          width={6}
          height={tableHeight}
          fill="transparent"
          onMouseEnter={(e) => {
            const container = e.target.getStage().container();
            container.style.cursor = 'col-resize';
          }}
          onMouseLeave={(e) => {
            if (resizingCol === null) {
              const container = e.target.getStage().container();
              container.style.cursor = 'default';
            }
          }}
          onMouseDown={() => handleColResizeStart(colWidths.length - 1)}
        />

        {/* Иконка перемещения (появляется при выборе таблицы) */}
        {isSelected && (
          <Group
            x={-12}
            y={-12}
            draggable={true}
            onDragMove={(e) => {
              // Обновляем позицию таблицы в реальном времени
              const iconPos = e.target.position();
              const newX = x + iconPos.x + 12;
              const newY = y + iconPos.y + 12;
              
              if (onTableStateChange) {
                onTableStateChange({ 
                  ...state, 
                  x: newX, 
                  y: newY 
                });
              }
            }}
            onDragEnd={(e) => {
              // Сбрасываем позицию иконки обратно
              e.target.position({ x: -12, y: -12 });
            }}
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'move';
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'default';
            }}
          >
            {/* Фон иконки */}
            <Circle
              x={0}
              y={0}
              radius={12}
              fill="#3f51b5"
              stroke="#ffffff"
              strokeWidth={2}
            />
            {/* Крестик (4 стрелки) */}
            <Line
              points={[-6, 0, 6, 0]}
              stroke="#ffffff"
              strokeWidth={2}
              lineCap="round"
            />
            <Line
              points={[0, -6, 0, 6]}
              stroke="#ffffff"
              strokeWidth={2}
              lineCap="round"
            />
            {/* Стрелки */}
            <Line
              points={[4, -1.5, 6, 0, 4, 1.5]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={[-4, -1.5, -6, 0, -4, 1.5]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={[-1.5, 4, 0, 6, 1.5, 4]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={[-1.5, -4, 0, -6, 1.5, -4]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
          </Group>
        )}
      </Group>
    </Group>
  );
};

export default WorkshopTable;
